import { TranzilaHostedFields } from 'config/abTests';
import { minutesToHours } from 'helpers/timeUtils';
import { flow } from 'lodash';
import { createSelector } from 'reselect';
import { PromotionPlanType } from 'store/sagas/apiService/types';
import { State } from 'store/state';
import { abTestsStateSelector } from 'store/state/ab-tests/selectors';
import { getActivePromotionPlansSelector, makeNullSafeDomainGetter, privateUserCurrentPromotionPlanV2DomainSelector } from 'store/state/domainData/selectors/common';
import { promotePrivateUserBulletinResponseSelector, newListingResponseSelector, editListingResponseSelector } from 'store/state/mutationsResponse/selectors';

const appStateSelector = (state: State) => state.app;
const privateUserMonetizationSelector = flow(appStateSelector, state => state.privateUserMonetization);

export const isPrivateBulletinSuccesfullyPromotedModalOpenSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinSuccesfullyPromotedModal.isOpen);
export const privateBulletinSuccesfullyPromotedModalBulletinIdSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinSuccesfullyPromotedModal.bulletinId);
export const isPrivateBulletinSuccesfullyPromotedPopoverSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinSuccesfullyPromotedPopover);
export const isPrivateBulletinHasAlreadyBeenPromotedModalSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinHasAlreadyBeenPromotedModal.isOpen);
export const privateBulletinHasAlreadyBeenPromotedModalBulletinIdSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinHasAlreadyBeenPromotedModal.bulletinId);
export const isPrivateBulletinSelectPromotionPlanModalSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinSelectPromotionPlanModal.isOpen);
export const privateBulletinSelectPromotionPlanModalBulletinIdSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinSelectPromotionPlanModal.bulletinId);
export const isPrivateBulletinMinimumPromotionPlanSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinMinimumPromotionPlanModal.isOpen);
export const privateBulletinMinimumPromotionPlanModalBulletinIdSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinMinimumPromotionPlanModal.bulletinId);
export const isPrivateBulletinPlanLoadingModalSelector = flow(privateUserMonetizationSelector, value => value.privateBulletinPlanLoadingModal.isOpen);
export const isRedeemPromotionPlanGrantResultModalOpenSelector = flow(privateUserMonetizationSelector, value => value.redeemPromotionPlanGrantResultModal.isOpen);
export const isCancelUserPromotionPlanResultModalOpenSelector = flow(privateUserMonetizationSelector, value => value.cancelUserPromotionPlanResultModal.isOpen);

export const isPromotePrivateBulletinMutationLoadingSelector = flow(promotePrivateUserBulletinResponseSelector, response => Boolean(response && response.loading));

/** Current account plan from userCurrentPromotionPlan API (accountPromotionPlan). Same shape as legacy currentPromotionPlan. */
export const currentPromotionPlanDomainDataSelector = flow(
  privateUserCurrentPromotionPlanV2DomainSelector,
  (domain) => {
    if (!domain || !domain.data || !domain.data.userCurrentPromotionPlan) return null;
    return domain.data.userCurrentPromotionPlan.accountPromotionPlan;
  }
);
export const userCurrentPromotionPlanDomainDataSelector = makeNullSafeDomainGetter(privateUserCurrentPromotionPlanV2DomainSelector, 'userCurrentPromotionPlan', true);
export const activePromotionPlansDataSelector = makeNullSafeDomainGetter(getActivePromotionPlansSelector, 'activePromotionPlans');


export const isUploadListingLoadingSelector = createSelector([
  newListingResponseSelector,
  editListingResponseSelector,
], (newListing, editListing) => {
  const newListingLoading = newListing ? newListing.loading : false;
  const editListingLoading = editListing ? editListing.loading : false;
  return newListingLoading || editListingLoading;
});


export const activePromotionPlanAutoUpdatePeriodSelector = flow(activePromotionPlansDataSelector, activePlans => activePlans && activePlans[0].intervalMinutes);
export const activePromotionPlanTypesSelector = flow(activePromotionPlansDataSelector, activePlans => (activePlans || []).map(plan => plan.type));
export const promotionMiniPlanSelector = flow(activePromotionPlansDataSelector, data => data ? data.find(plan => plan.type === PromotionPlanType.MINI) : null);
export const isBasicAccountLevelPromotionPlanSelector = flow(currentPromotionPlanDomainDataSelector, data => data && data.promotionPlan.type === PromotionPlanType.BASIC); // Needed to sunset of account level plans; Can be removed afterwards.
export const bulletinPromotionPlanSelector = createSelector(
  userCurrentPromotionPlanDomainDataSelector,
  (_: State, bulletinId: string) => bulletinId,
  (data, bulletinId) => {
    if (!data) return null;
    const bulletinPlan = data.bulletinPromotionPlans.find(plan => plan.bulletinId === bulletinId);
    return bulletinPlan || data.accountPromotionPlan;
  }
);
export const makeIsBulletinBasicPromotionSelector = (bulletinId: string) => createSelector(
  (state: State) => bulletinPromotionPlanSelector(state, bulletinId),
  plan => plan && plan.promotionPlan.type === PromotionPlanType.BASIC
);

export const upcomingAutoPromotionTimeInfoByBulletinIdSelector = createSelector(
  userCurrentPromotionPlanDomainDataSelector,
  (_: State, bulletinId: string) => bulletinId,
  (data, bulletinId) => {
    if (!data) return null;
    const bulletinPlan = data.bulletinPromotionPlans.find(plan => plan.bulletinId === bulletinId);
    const currentBulletinPlan = bulletinPlan || data.accountPromotionPlan;
    if (!currentBulletinPlan) return null;

    return {
      intervalMinutes: currentBulletinPlan.promotionPlan.intervalMinutes,
      autoPromotionDates:  'upcomingAutoPromotionDates' in  currentBulletinPlan ? currentBulletinPlan.upcomingAutoPromotionDates : [],
    };
  }
);

export const currentPromotionPlanTypeSelector = flow(currentPromotionPlanDomainDataSelector, data => data && data.promotionPlan.type);
export const currentPromotionPlanIntervalMinutesSelector = flow(currentPromotionPlanDomainDataSelector, data => data && data.promotionPlan.intervalMinutes);
export const currentPromotionPlanIntervalHoursSelector = createSelector(currentPromotionPlanIntervalMinutesSelector, minutes => {
  return minutesToHours(minutes);
});
export const currentPromotionPlanIsAboutToExpire = flow(currentPromotionPlanDomainDataSelector, data => {
  if (!data) return null;
  const now = new Date();
  const end = new Date(data.endDate);

  const oneDayBeforeEnd = new Date(end);
  oneDayBeforeEnd.setDate(oneDayBeforeEnd.getDate() - 1);

  return now >= oneDayBeforeEnd && now < end;
});
export const latestManualPromotionsSelector = createSelector(currentPromotionPlanDomainDataSelector, data => data && data.latestManualPromotions);

export const createNextAvailablePromotionDateSelector = (bulletinId: string) => createSelector(currentPromotionPlanDomainDataSelector, data => {
  if (!data) {
    return null;
  }

  const bulletinPromotionInfo = data.latestManualPromotions && data.latestManualPromotions.find(promotion => promotion.bulletinId === bulletinId);

  if (!bulletinPromotionInfo) {
    // Was not promoted yet
    return null;
  }

  return new Date(bulletinPromotionInfo.promotedUntil);
});

export const isTranzilaHostedFieldsModeSelector = createSelector([ abTestsStateSelector ], ({ tranzilaHostedFields }) => tranzilaHostedFields === TranzilaHostedFields.modeB);
